'use strict';

const adformConstants = require('../adformConstants');
const adformConfigValidator = require('./adformConfigValidator');

const ASSET_TITLE = {
    required: 0,
    id: adformConstants.ASSET_ID_TITLE,
    title: {
        len: 10000
    }
};

const ASSET_DESCRIPTION = {
    required: 1,
    id: adformConstants.ASSET_ID_DESCRIPTION,
    data: {
        type: 2,
        len: 10000
    }
};

/**
 * Creates desktop img asset object for request (width, height, if it's required...).
 * Desktop img in adform response bids will have these requirements.
 *
 * @param {string} width - img width
 * @param {string} height - img height
 * @return {Object} - desktop img asset
 */
function createDesktopImgAsset(width, height) {
    return {
        required: 1,
        id: adformConstants.ASSET_ID_DESKTOP_IMG,
        img: {
            type: 3,
            w: width,
            h: height
        }
    };
}

/**
 * Creates mobile img asset object for request (width, height, if it's required...).
 * Mobile img in adform response bids will have these requirements.
 * @param {string} width - img width
 * @param {string} height - img height
 * @return {Object} - mobile img asset
 */
function createMobileImgAsset(width, height) {
    return {
        required: 1,
        id: adformConstants.ASSET_ID_MOBILE_IMG,
        img: {
            type: 1,
            w: width,
            h: height
        }
    };
}

/**
 * Generate random unique string
 * @return {string} unique string
 */
function uuidv4() {
    /* eslint-disable */
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
        (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
    /* eslint-enable */
}

/**
 * Generates object used for adform request to get ad bids (goal: to show ads for specific page)
 * @param {Object} obj - object with
 * @return {Object|null} - request object
 */
function buildRequestObject(obj) {
    if (!obj) {
        return null;
    }

    if (!obj.sitePage
        || !obj.siteDomain
        || !obj.buyeruid
        || !obj.deviceUA
        || !obj.imp
        || obj.imp.length === 0
    ) {
        return null;
    }

    return {
        id: uuidv4(),
        ext: {
            adfmultibid: true
        },
        site: {
            page: obj.sitePage,
            domain: obj.siteDomain
        },
        user: {
            buyeruid: obj.buyeruid
        },
        device: {
            ua: obj.deviceUA,
            ip: obj.deviceIP
        },
        cur: [
            'EUR'
        ],
        imp: obj.imp
    };
}

/**
 * Private function to build imp for request based on container type (banner, carousel...) for bids.
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @return {Array} - imp
 */
function buildRequestImp(containerTypeConfig) {
    if (!adformConfigValidator.checkMandatoryFields(containerTypeConfig)) {
        return [];
    }

    try {
        const desktopImgObj = containerTypeConfig.desktop;
        const mobileImgObj = containerTypeConfig.mobile;

        const requestImpAssets = [
            ASSET_TITLE,
            ASSET_DESCRIPTION,
            createDesktopImgAsset(desktopImgObj.w, desktopImgObj.h)
        ];

        if (containerTypeConfig.isMobileImgRequired) {
            requestImpAssets.push(
                createMobileImgAsset(mobileImgObj.w, mobileImgObj.h)
            );
        }

        return [
            {
                tagid: containerTypeConfig.tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

/**
 * Private function to build imp for product request.
 * @param {string} tagId - product request tagId
 * @return {Array} - imp
 */
function buildProductRequestImp(tagId) {
    if (!tagId) {
        return [];
    }

    try {
        const requestImpAssets = [
            ASSET_TITLE,
            ASSET_DESCRIPTION
        ];

        return [
            {
                tagid: tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

/**
 * Private function to build imp for "featured block" request.
 * @param {string} tagId - request tagId
 * @return {Array} - imp
 */
function buildFeaturedBlockRequestImp(tagId) {
    if (!tagId) {
        return [];
    }

    try {
        const desktopImgObj = adformConstants.FEATURED_BLOCK_DESKTOP;
        const mobileImgObj = adformConstants.FEATURED_BLOCK_MOBILE;

        const requestImpAssets = [
            ASSET_TITLE,
            ASSET_DESCRIPTION,
            createDesktopImgAsset(desktopImgObj.w, desktopImgObj.h),
            createMobileImgAsset(mobileImgObj.w, mobileImgObj.h)
        ];

        return [
            {
                tagid: tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

/**
 * Private function to build imp for "featured block" request.
 * @param {string} tagId - request tagId
 * @return {Array} - imp
 */
function buildProductTileBannerRequestImp(tagId) {
    if (!tagId) {
        return [];
    }
    try {
        const mobileImgObj = adformConstants.LISTPAGE_TILE_BANNER_MOBILE;
        const desktopImgObj = adformConstants.LISTPAGE_TILE_BANNER_DESKTOP;
        const requestImpAssets = [
            createMobileImgAsset(mobileImgObj.w, mobileImgObj.h),
            createDesktopImgAsset(desktopImgObj.w, desktopImgObj.h)
        ];

        return [
            {
                tagid: tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

/**
 * Private function to build imp for "homepage recommended slider products" request.
 * @return {Array} - imp
 */
function buildHomepageRecommendationProductsRequestImp() {
    const tagId = adformConstants.HOMEPAGE_RECOMMENDED_PRODUCTS_TAG_ID;
    if (!tagId) {
        return [];
    }

    try {
        const requestImpAssets = [
            ASSET_DESCRIPTION
        ];

        return [
            {
                tagid: tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

/**
 * Private function to build imp for "pdp products slider" request.
 * @param {string} tagId - request tagId
 * @return {Array} - imp
 */
function buildPDPProductsSliderRequestImp(tagId) {
    if (!tagId) {
        return [];
    }

    try {
        const requestImpAssets = [
            ASSET_DESCRIPTION
        ];

        return [
            {
                tagid: tagId,
                native: {
                    request: {
                        assets: requestImpAssets
                    }
                }
            }
        ];
    } catch (e) {
        return [];
    }
}

module.exports = {
    buildRequestObject: buildRequestObject,
    buildRequestImp: buildRequestImp,
    buildProductRequestImp: buildProductRequestImp,
    buildFeaturedBlockRequestImp: buildFeaturedBlockRequestImp,
    buildProductTileBannerRequestImp: buildProductTileBannerRequestImp,
    buildHomepageRecommendationProductsRequestImp: buildHomepageRecommendationProductsRequestImp,
    buildPDPProductsSliderRequestImp: buildPDPProductsSliderRequestImp
};
